import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { QRCodeCanvas } from 'qrcode.react';
import {
  Camera, Loader2, RotateCcw, CheckCircle2, User, Phone, Mail,
  MessageCircle, Tag, StickyNote, LogOut, ArrowRight, Send, Copy,
  KeyRound, Users,
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;
const KIOSK_TOKEN_KEY = 'wekeza_kiosk_token';
const KIOSK_REP_KEY = 'wekeza_kiosk_rep';

/** Normalize any TZ phone typed in. */
const normalizeTzPhone = (raw) => {
  if (!raw) return '';
  const s = String(raw).replace(/[^\d+]/g, '');
  if (s.startsWith('+255') && s.length === 13) return s;
  if (s.startsWith('255') && s.length === 12) return `+${s}`;
  if (s.startsWith('0') && s.length === 10) return `+255${s.slice(1)}`;
  if (s.length === 9 && s.startsWith('7')) return `+255${s}`;
  return s;
};

const emptyLead = {
  name: '',
  phone: '',
  whatsapp_phone: '',
  email: '',
  interest_tags: [],
  notes: '',
  confidence: null,
};

// ────────────────────────────────────────────────────────────────
// PIN LOGIN
// ────────────────────────────────────────────────────────────────
function PinLogin({ onAuthed }) {
  const [pin, setPin] = useState('');
  const [repName, setRepName] = useState(localStorage.getItem(KIOSK_REP_KEY) || '');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/kiosk/pin-login`, { pin, rep_name: repName });
      localStorage.setItem(KIOSK_TOKEN_KEY, data.kiosk_token);
      localStorage.setItem(KIOSK_REP_KEY, repName);
      onAuthed(data.kiosk_token);
      toast.success(`Kiosk unlocked — welcome ${repName || 'rep'}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'PIN login failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-900 via-emerald-800 to-emerald-950 text-white flex items-center justify-center px-4">
      <div className="w-full max-w-sm bg-white/5 backdrop-blur border border-white/10 rounded-3xl p-8">
        <div className="text-center mb-6">
          <KeyRound className="w-10 h-10 mx-auto text-emerald-300" />
          <h1 className="text-2xl font-bold mt-3">Wekeza-Smart Kiosk</h1>
          <p className="text-sm text-emerald-100/80 mt-1">Enter your device PIN to start capturing leads.</p>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-wider text-emerald-200/80 mb-1.5">Your name</label>
            <input
              type="text"
              value={repName}
              onChange={(e) => setRepName(e.target.value)}
              className="w-full h-11 px-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-emerald-300"
              placeholder="Brian at Booth 12"
              data-testid="kiosk-rep-name"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-emerald-200/80 mb-1.5">Device PIN</label>
            <input
              type="password"
              inputMode="numeric"
              autoFocus
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
              maxLength={8}
              className="w-full h-14 px-3 rounded-xl bg-white/10 border border-white/20 text-white text-center text-2xl tracking-[0.5em] focus:outline-none focus:ring-2 focus:ring-emerald-300"
              placeholder="••••"
              data-testid="kiosk-pin-input"
              required
            />
          </div>
          <Button
            type="submit"
            disabled={busy || pin.length < 4}
            className="w-full h-12 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold"
            data-testid="kiosk-pin-submit"
          >
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Unlocking…</> : 'Unlock kiosk'}
          </Button>
        </form>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// MAIN CAPTURE FLOW
// ────────────────────────────────────────────────────────────────
function CaptureFlow({ kioskToken, onLogout }) {
  const fileRef = useRef(null);
  const [phase, setPhase] = useState('idle'); // idle | scanning | review | submitting | done
  const [imagePreview, setImagePreview] = useState(null);
  const [lead, setLead] = useState(emptyLead);
  const [result, setResult] = useState(null); // { magic_url, channels_sent, already_existed }

  const authHeader = { 'X-Kiosk-Token': kioskToken };

  const reset = () => {
    setPhase('idle');
    setImagePreview(null);
    setLead(emptyLead);
    setResult(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleFile = async (file) => {
    if (!file) return;
    setPhase('scanning');
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
      });
      setImagePreview(dataUrl);
      const { data } = await axios.post(
        `${API}/api/kiosk/scan-card`,
        { image_base64: dataUrl },
        { headers: authHeader, timeout: 45000 },
      );
      const e = data.extracted || {};
      setLead({
        name: e.name || '',
        phone: e.phone || '',
        whatsapp_phone: e.whatsapp_phone || e.phone || '',
        email: e.email || '',
        interest_tags: e.interest_tags || [],
        notes: e.notes || '',
        confidence: e.confidence,
      });
      setPhase('review');
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Card scan failed';
      toast.error(String(msg));
      // Still let the rep type manually — jump to review with empty fields.
      setLead(emptyLead);
      setPhase('review');
    }
  };

  const onSkipCamera = () => {
    setImagePreview(null);
    setLead(emptyLead);
    setPhase('review');
  };

  const canSubmit =
    lead.name.trim().length > 1 &&
    (normalizeTzPhone(lead.whatsapp_phone).length >= 10 ||
      normalizeTzPhone(lead.phone).length >= 10 ||
      (lead.email || '').includes('@'));

  const submit = async () => {
    if (!canSubmit || phase === 'submitting') return;
    setPhase('submitting');
    try {
      const payload = {
        name: lead.name.trim(),
        phone: normalizeTzPhone(lead.phone) || null,
        whatsapp_phone: normalizeTzPhone(lead.whatsapp_phone) || normalizeTzPhone(lead.phone) || null,
        email: (lead.email || '').trim().toLowerCase() || null,
        interest_tags: lead.interest_tags,
        notes: lead.notes,
        lead_source: 'trade_fair_2026',
        send_whatsapp: true,
        send_email: !!(lead.email || '').trim(),
      };
      const { data } = await axios.post(`${API}/api/kiosk/register-lead`, payload, { headers: authHeader });
      setResult(data);
      setPhase('done');
      toast.success(data.already_existed ? 'Existing account re-linked' : 'Lead registered — magic link sent');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Registration failed');
      setPhase('review');
    }
  };

  // ── Idle screen ─────────────────────────────────────────────
  if (phase === 'idle') {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <header className="flex items-center justify-between px-4 py-3 bg-white border-b border-slate-200">
          <span className="text-sm font-semibold text-slate-800">Kiosk — {localStorage.getItem(KIOSK_REP_KEY) || 'Rep'}</span>
          <button onClick={onLogout} className="text-xs text-slate-500 flex items-center gap-1" data-testid="kiosk-logout">
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </header>
        <div className="flex-1 flex items-center justify-center px-6 py-8">
          <div className="w-full max-w-sm text-center">
            <div className="w-24 h-24 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
              <Camera className="w-11 h-11 text-emerald-700" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mt-6">New lead</h1>
            <p className="text-sm text-slate-600 mt-2">
              Snap a photo of their card, our AI reads name, phone and interests, then you review and send.
            </p>
            <label className="mt-8 w-full inline-flex items-center justify-center gap-2 h-14 rounded-2xl bg-emerald-700 text-white font-semibold shadow-lg active:scale-95 transition-transform cursor-pointer" data-testid="kiosk-scan-btn">
              <Camera className="w-5 h-5" /> Scan a card
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={(e) => handleFile(e.target.files?.[0])}
                className="hidden"
              />
            </label>
            <button onClick={onSkipCamera} className="mt-4 text-sm text-emerald-700 underline" data-testid="kiosk-manual-btn">
              Or type it in manually
            </button>

            <div className="mt-8 pt-6 border-t border-slate-200">
              <p className="text-xs text-slate-500 mb-2">Bulk sign-in sheet?</p>
              <a
                href="/kiosk/roster"
                className="inline-flex items-center justify-center gap-2 h-11 px-4 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-semibold text-sm shadow"
                data-testid="kiosk-roster-btn"
              >
                <Users className="w-4 h-4" /> Roster mode
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Scanning ─────────────────────────────────────────────
  if (phase === 'scanning') {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center px-6">
        {imagePreview && <img src={imagePreview} alt="scan" className="max-h-64 rounded-xl shadow mb-6" />}
        <Loader2 className="w-8 h-8 text-emerald-700 animate-spin" />
        <p className="mt-3 text-sm text-slate-700 font-medium">Reading the card…</p>
        <p className="text-xs text-slate-500 mt-1">Usually 3–5 seconds.</p>
      </div>
    );
  }

  // ── Review / edit ─────────────────────────────────────────────
  if (phase === 'review' || phase === 'submitting') {
    const toggleTag = (t) => setLead((L) => ({
      ...L,
      interest_tags: L.interest_tags.includes(t)
        ? L.interest_tags.filter((x) => x !== t)
        : [...L.interest_tags, t],
    }));
    const quickTags = ['DSE stocks', 'UTT AMIS', 'government bonds', 'learning first', 'small budget'];
    return (
      <div className="min-h-screen bg-slate-50 pb-32">
        <header className="flex items-center justify-between px-4 py-3 bg-white border-b border-slate-200 sticky top-0 z-10">
          <button onClick={reset} className="text-sm text-slate-600 flex items-center gap-1" data-testid="kiosk-back">
            <RotateCcw className="w-4 h-4" /> Start over
          </button>
          {lead.confidence != null && (
            <span className={`text-xs px-2 py-0.5 rounded-full ${lead.confidence >= 0.7 ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
              AI confidence: {Math.round(lead.confidence * 100)}%
            </span>
          )}
        </header>
        <div className="max-w-md mx-auto px-4 py-4 space-y-3">
          {imagePreview && <img src={imagePreview} alt="card" className="w-full max-h-48 object-contain rounded-xl border border-slate-200 bg-white" />}
          <Field icon={<User className="w-4 h-4" />} label="Name *" value={lead.name} onChange={(v) => setLead({ ...lead, name: v })} placeholder="Amina Hassan" testid="kiosk-name" />
          <Field icon={<MessageCircle className="w-4 h-4 text-emerald-700" />} label="WhatsApp number * (primary CTA)" value={lead.whatsapp_phone} onChange={(v) => setLead({ ...lead, whatsapp_phone: v })} placeholder="0754 710 139" testid="kiosk-whatsapp" tel />
          <Field icon={<Phone className="w-4 h-4" />} label="Phone (SMS)" value={lead.phone} onChange={(v) => setLead({ ...lead, phone: v })} placeholder="Same as WhatsApp if none written" testid="kiosk-phone" tel />
          <Field icon={<Mail className="w-4 h-4" />} label="Email (optional)" value={lead.email} onChange={(v) => setLead({ ...lead, email: v })} placeholder="amina@example.com" testid="kiosk-email" />
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5 flex items-center gap-1"><Tag className="w-3.5 h-3.5" /> Interest tags</label>
            <div className="flex flex-wrap gap-1.5">
              {[...new Set([...quickTags, ...lead.interest_tags])].map((t) => (
                <button
                  key={t}
                  onClick={() => toggleTag(t)}
                  className={`px-3 py-1 rounded-full text-xs border ${lead.interest_tags.includes(t) ? 'bg-emerald-700 text-white border-emerald-700' : 'bg-white text-slate-700 border-slate-300'}`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
          <Field icon={<StickyNote className="w-4 h-4" />} label="Notes" value={lead.notes} onChange={(v) => setLead({ ...lead, notes: v })} placeholder="Anything the rep wants remembered" testid="kiosk-notes" multiline />
        </div>
        <div className="fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 p-3">
          <div className="max-w-md mx-auto">
            <Button
              onClick={submit}
              disabled={!canSubmit || phase === 'submitting'}
              className="w-full h-13 rounded-2xl bg-emerald-700 hover:bg-emerald-800 text-white font-semibold text-base"
              data-testid="kiosk-submit"
            >
              {phase === 'submitting'
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending link…</>
                : <><Send className="w-4 h-4 mr-2" /> Register + send CTA</>}
            </Button>
            {!canSubmit && (
              <p className="text-[11px] text-slate-500 text-center mt-1.5">
                Need name plus at least one of: WhatsApp, phone, or email.
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Done — show QR code ─────────────────────────────────────────────
  if (phase === 'done') {
    const url = result?.magic_url || '';
    const channels = result?.channels_sent || {};
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-white flex flex-col">
        <header className="flex items-center justify-end px-4 py-3">
          <button onClick={onLogout} className="text-xs text-slate-500 flex items-center gap-1">
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </header>
        <div className="flex-1 flex items-center justify-center px-6">
          <div className="w-full max-w-sm text-center">
            <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-700" />
            <h1 className="text-2xl font-bold text-slate-900 mt-3" data-testid="kiosk-done">
              {result?.already_existed ? 'Welcome back!' : 'Registered!'}
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              {result?.already_existed
                ? 'This client already had an account — same link works.'
                : 'Point the client\'s phone camera at the QR to open the app already logged in.'}
            </p>
            <div className="mt-6 p-4 bg-white rounded-2xl shadow-lg border border-slate-100 inline-block">
              {url && <QRCodeCanvas value={url} size={220} level="M" includeMargin data-testid="kiosk-qr" />}
            </div>
            <div className="mt-4 flex justify-center gap-2 text-xs">
              <ChannelBadge label="WhatsApp" ok={channels.whatsapp} />
              <ChannelBadge label="Email" ok={channels.email} />
              <ChannelBadge label="QR" ok={true} />
            </div>
            <button
              onClick={() => navigator.clipboard.writeText(url).then(() => toast.success('Link copied'))}
              className="mt-4 inline-flex items-center gap-1 text-xs text-emerald-700 underline"
              data-testid="kiosk-copy-link"
            >
              <Copy className="w-3 h-3" /> Copy link
            </button>
            <Button onClick={reset} className="mt-8 w-full h-13 rounded-2xl bg-emerald-700 hover:bg-emerald-800 text-white font-semibold" data-testid="kiosk-next">
              <ArrowRight className="w-4 h-4 mr-1" /> Next lead
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

// ────────────────────────────────────────────────────────────────
function Field({ icon, label, value, onChange, placeholder, testid, multiline, tel }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1.5 flex items-center gap-1">{icon} {label}</label>
      {multiline ? (
        <textarea
          rows={2}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full px-3 py-2 rounded-xl border border-slate-300 focus:border-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-100 text-sm"
          data-testid={testid}
        />
      ) : (
        <input
          type={tel ? 'tel' : 'text'}
          inputMode={tel ? 'tel' : 'text'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full h-11 px-3 rounded-xl border border-slate-300 focus:border-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-100 text-sm"
          data-testid={testid}
        />
      )}
    </div>
  );
}

function ChannelBadge({ label, ok }) {
  return (
    <span className={`px-2 py-0.5 rounded-full border ${ok ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : 'bg-slate-100 text-slate-500 border-slate-200'}`}>
      {ok ? '✓' : '·'} {label}
    </span>
  );
}

// ────────────────────────────────────────────────────────────────
export default function Kiosk() {
  const [kioskToken, setKioskToken] = useState(() => localStorage.getItem(KIOSK_TOKEN_KEY) || null);
  const logout = () => {
    localStorage.removeItem(KIOSK_TOKEN_KEY);
    setKioskToken(null);
  };
  if (!kioskToken) return <PinLogin onAuthed={setKioskToken} />;
  return <CaptureFlow kioskToken={kioskToken} onLogout={logout} />;
}
